// ---------------------------------------------------------------------------
// DOM plumbing for the guide engine: finding a target that has not painted yet,
// measuring it, and getting it on screen.
//
// The old implementation polled every 100ms for 4 seconds and then silently gave
// up, leaving a centered card with no explanation. This one observes mutations
// (so it lands the same frame the element appears), keeps a slow interval as a
// safety net for layout-only changes an observer never sees, and reports the
// timeout to the caller so the engine can say something honest.
// ---------------------------------------------------------------------------

const SAFETY_INTERVAL_MS = 250;
const DEFAULT_TIMEOUT_MS = 8000;

// An element that exists but has no box (display:none, an unmounted Collapse,
// a tab that is not selected) is not something we can point at yet.
function isPaintable(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0;
}

// All matches, not the first. Several admin pages mount a phone header and a
// desktop header at once and hide one with `display`, so the first element
// carrying the anchor is routinely the invisible one.
export function findTarget(selector) {
  if (!selector || selector === 'body') return document.body;
  const all = document.querySelectorAll(selector);
  for (let i = 0; i < all.length; i += 1) {
    if (isPaintable(all[i])) return all[i];
  }
  return null;
}

export function readRect(selector) {
  if (!selector || selector === 'body') return null;
  const el = findTarget(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return r;
}

/**
 * Wait for `selector` to exist and be paintable.
 *
 * @returns {() => void} cancel. `onSettle(found)` fires exactly once.
 */
export function waitForTarget(selector, onSettle, { timeout = DEFAULT_TIMEOUT_MS } = {}) {
  let settled = false;

  const finish = (found) => {
    if (settled) return;
    settled = true;
    cleanup();
    onSettle(found);
  };

  const check = () => {
    if (findTarget(selector)) finish(true);
  };

  const observer = new MutationObserver(check);
  const interval = setInterval(check, SAFETY_INTERVAL_MS);
  const timer = setTimeout(() => finish(false), timeout);

  function cleanup() {
    observer.disconnect();
    clearInterval(interval);
    clearTimeout(timer);
  }

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'data-tour'],
  });

  check(); // it may already be there

  return () => {
    if (settled) return;
    settled = true;
    cleanup();
  };
}

// The nearest ancestor that actually scrolls. Targets inside a scrollable panel
// need that panel scrolled, not the window, or the ring lands off screen.
function scrollParent(el) {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY, overflowX } = window.getComputedStyle(node);
    const scrolls = /(auto|scroll|overlay)/.test(overflowY + overflowX);
    if (scrolls && (node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function bringIntoView(selector) {
  if (!selector || selector === 'body') return;
  const el = findTarget(selector);
  if (!el) return;

  const parent = scrollParent(el);
  if (parent) {
    const elRect = el.getBoundingClientRect();
    const boxRect = parent.getBoundingClientRect();
    const above = elRect.top < boxRect.top;
    const below = elRect.bottom > boxRect.bottom;
    if (above || below) {
      parent.scrollTop += elRect.top - boxRect.top - (boxRect.height - elRect.height) / 2;
    }
  }

  el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
}

/**
 * Click the real element behind a target. Used only by "Do it for me" on steps
 * that navigate or open a dialog - never on anything that writes a record.
 */
export function clickTarget(selector) {
  const el = findTarget(selector);
  if (!el) return false;
  // Prefer an inner control: anchors often wrap the actual button, and MUI
  // wraps disabled buttons in a span that swallows the click.
  const clickable = el.matches('button, a, [role="button"], input, select')
    ? el
    : el.querySelector('button, a, [role="button"]') || el;
  clickable.click();
  return true;
}
